// Shared, short-TTL cache for the heavy AWS-ops pipeline snapshot (ADM-3).
//
// The `/pipeline` snapshot is the console's single most expensive call
// (ListFunctions + ListRules + queue attrs + 24h CloudWatch metric series PER
// lambda). The Logs page only needs it to populate the log-group PICKER, yet was
// re-firing the whole snapshot on every mount / drill-down. Pipeline + Logs now
// share this module-scope cache, so visiting Pipeline warms the picker for Logs
// (and repeated Logs mounts within the TTL cost nothing).
//
// Pipeline itself always fetches fresh (force:true) — it's a live monitoring
// view and its Refresh/error semantics must not be masked by the cache — but it
// still PRIMES the cache for Logs as a side effect.
import { invokeOps } from "./adminApi";
import type { PipelineSnapshot } from "./types";

const TTL_MS = 30_000;

let cache: { snap: PipelineSnapshot; at: number } | null = null;
let inflight: Promise<PipelineSnapshot> | null = null;

/**
 * Return the pipeline snapshot, served from the module cache when fresh.
 * `force` bypasses the cache (used by the Pipeline page's live fetch + Refresh);
 * every successful fetch (forced or not) refreshes the cache for later readers.
 */
export async function getPipelineSnapshot(opts?: { force?: boolean }): Promise<PipelineSnapshot> {
  const force = opts?.force ?? false;
  const now = Date.now();

  if (!force) {
    if (cache && now - cache.at < TTL_MS) return cache.snap;
    if (inflight) return inflight;
  }

  const p = invokeOps<PipelineSnapshot>("/pipeline")
    .then((snap) => {
      cache = { snap, at: Date.now() };
      return snap;
    })
    .finally(() => {
      if (inflight === p) inflight = null;
    });

  if (!force) inflight = p;
  return p;
}
